import React, { ReactNode, useContext } from 'react';
import { Breadcrumb, BreadcrumbProps } from '../../components/Breadcrumb';
import { Image } from '../../components/Image';
import { ImageProps } from '../../components/Image/types';
import {
  TrustpilotWidget,
  TrustpilotWidgetProps,
} from '../../components/TrustpilotWidget';
import { BrandSystemContext } from '../../contexts/BrandSystemContext';
import { container } from '../../styles/themes';
import { textBodyLarge, textSerifTitle1 } from '../../styles/typography';
import { DefaultBsComponent } from '../../types/module';
import { classNames } from '../../utils/style';
import { applyBgColor } from '../../utils/utils';
import { ClientHero, ClientProps } from './ClientHero';

export interface HeroProps extends DefaultBsComponent {
  backgroundColor:
  | 'blue'
  | 'mint'
  | 'purple'
  | 'coral'
  | 'azur'
  | 'yellow'
  | 'white';
  button?: ReactNode;
  additionalButton?: ReactNode[];
  className?: string;
  image?: ImageProps;
  title: ReactNode;
  subtitle?: ReactNode;
  isClientHero?: boolean;
  heroClientProps?: ClientProps;
  breadcrumbs?: BreadcrumbProps;
  trustpilotWidget?: TrustpilotWidgetProps[];
}

export const Hero = ({
  backgroundColor = 'white',
  button,
  additionalButton,
  className,
  image,
  title,
  subtitle,
  isClientHero,
  heroClientProps,
  breadcrumbs,
  children,
  trustpilotWidget,
}: HeroProps) => {
  const textOnly = !image || isClientHero;
  const appliedBackgroundColor = isClientHero ? 'white' : backgroundColor;
  const context = useContext(BrandSystemContext);

  return (
    <section
      className={classNames(
        'Hero',
        applyBgColor(appliedBackgroundColor),
        className !== undefined ? className : '',
        'overflow-hidden',
        !isClientHero
          ? 'pt-[64px] pb-[64px] md:pt-[104px] md:pb-[104px]'
          : 'pt-[64px] md:pt-[104px]',
      )}
    >
      <div
        className={classNames(
          container,
          'flex flex-col items-center',
          textOnly ? 'text-center' : 'lg:flex-row',
        )}
      >
        <div
          className={classNames(
            !textOnly && 'mb-40 lg:mr-24',
            'lg:mb-0 lg:max-w-[483px] lg:w-full xl:max-w-[915px]',
            'flex-1',
          )}
        >
          {breadcrumbs && (
            <div className="mb-24">
              <Breadcrumb hasFirstChevron={true} objectClass={{
                chevron: 'text-neutral-301',
                text: 'text-neutral-301',
                textActive: 'text-neutral-101',
              }} {...breadcrumbs} />
            </div>
          )}
          <div className="overflow-hidden">
            <h1
              className={classNames(
                textSerifTitle1,
                'mb-24 text-neutral-301',
                !textOnly && 'pr-24',
              )}
            >
              {title}
            </h1>
          </div>
          {subtitle && (
            <p
              className={classNames(
                textBodyLarge,
                'HeroSubtitle',
                'mb-24 text-neutral-101',
                !textOnly && 'pr-24',
              )}
            >
              {subtitle}
            </p>
          )}
          {(button || additionalButton) && (
            <div
              className={classNames(
                'flex flex-col gap-12 md:flex-row',
                textOnly && 'justify-center',
              )}
            >
              {button && button}
              {additionalButton && additionalButton}
            </div>
          )}
          {!!trustpilotWidget?.length &&
            context?.language === 'es' && ( // prevent use of trustpilot if locale isn't Spain
              <TrustpilotWidget
                {...trustpilotWidget[0]}
                className="mt-24 md:mt-48"
                backgroundColor={appliedBackgroundColor}
              />
            )}
        </div>

        {!textOnly && (
          <div className={`relative flex items-center 'h-full w-full flex-1`}>
            {image && (
              <div className="flex justify-center w-full ">
                <Image
                  {...image}
                  className="w-full h-full rounded-18"
                  sizes={[
                    '(max-width: 380px) 350px',
                    '(max-width: 768) 718px',
                    '(max-width: 1280px) 493px',
                    '589px',
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {isClientHero && image && (
        <ClientHero {...heroClientProps} image={image} />
      )}

      {children}
    </section>
  );
};
